/**
 * Command-label main parser (ticket 3525, upstream pin e3e7cc3f, MIT), split
 * out of the ported `commandLabel.ts`. Byte-faithful.
 *
 * @module workLogCommandParse
 */

import {
  COMMAND_WRAPPER_FLAGS,
  COMMAND_WRAPPER_OPTIONS_WITH_VALUE,
  NON_DESCRIPTIVE_SHELL_PROGRAMS,
  NON_PROGRAM_PREFIX_CHARACTERS,
  NON_PROGRAM_SUFFIX_CHARACTERS,
  POWERSHELL_SETUP_PROGRAMS,
  SHELL_COMMAND_WRAPPERS,
  SHELL_PRECOMMAND_MODIFIERS,
  SHELL_PROGRAMS,
  SKIPPABLE_SUDO_PROBES,
  TERMINAL_SHELL_PROGRAMS,
  WINDOWS_SHELL_PROGRAMS,
  shellCommandArgumentIndex,
  tokenizeShellCommand,
  type CommandProgramContext,
} from "./workLogCommandTokens";
import {
  commandWithoutLeadingShellComments,
  indexAfterShellRedirection,
  serializeShellTokens,
  splitFirstShellCommand,
  withoutShellLineContinuations,
} from "./workLogCommandSplit";
import {
  powerShellAssignmentProgramName,
  startProcessProgramName,
  staticProgramName,
  windowsShellPayloadProgramName,
} from "./workLogCommandPrograms";
import { wrappedShellCommandProgramName } from "./workLogCommandWrappers";
import { transparentWrapperCommandIndex } from "./workLogCommandPrograms";
import type { CommandWrapper } from "./workLogCommandTokens";
import { commandProgramNameInternal } from "./workLogCommandDispatcher";

// fallow-ignore-next-line complexity -- port of upstream commandLabel.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function parseCommandProgramName(
  command: string,
  depth: number,
  context: CommandProgramContext,
  segmentsRemaining: number,
): string | null {
  if (depth >= 8 || segmentsRemaining <= 0) return null;
  const commandWithoutComments = commandWithoutLeadingShellComments(command);
  if (commandWithoutComments === null) return null;
  if (
    /^(?:catch|finally|for|foreach|function|if|param|switch|try|while)\s*[{(]/iu.test(
      commandWithoutComments,
    )
  ) {
    return null;
  }
  if (/^[A-Za-z_][A-Za-z0-9_]*\s*\(\s*\)\s*\{/u.test(commandWithoutComments)) return null;
  // `&&` and `||` inside a `[[ ... ]]` expression are not top-level command
  // separators. Keep the label conservative instead of scanning the test body.
  if (commandWithoutComments.startsWith("[[")) return null;
  const commandSplit = splitFirstShellCommand(commandWithoutComments);
  if (/^@["'](?:\r?\n)/u.test(commandSplit.firstCommand.trimStart())) {
    return commandSplit.remainingCommand
      ? commandProgramNameInternal(
          commandSplit.remainingCommand,
          depth,
          "shell",
          segmentsRemaining - 1,
        )
      : null;
  }
  const powerShellAssignment = powerShellAssignmentProgramName(
    commandSplit.firstCommand,
    depth,
    commandSplit.remainingCommand,
    segmentsRemaining,
  );
  if (powerShellAssignment.matched) return powerShellAssignment.program;
  const windowsPath = commandSplit.firstCommand.match(
    /^\s*((?:\.{1,2}|%[A-Za-z_][A-Za-z0-9_]*%|\$env:[A-Za-z_][A-Za-z0-9_]*)\\\S+)/iu,
  )?.[1];
  if (windowsPath) return staticProgramName(windowsPath);
  const tokens = tokenizeShellCommand(withoutShellLineContinuations(commandSplit.firstCommand));
  if (tokens === null) return null;
  const firstCharacter = commandSplit.firstCommand.trimStart()[0];
  if (
    tokens.length === 1 &&
    (firstCharacter === '"' || firstCharacter === "'") &&
    /[\s()=]/u.test(tokens[0] ?? "") &&
    !/[\\/]/u.test(tokens[0] ?? "")
  ) {
    return commandSplit.remainingCommand
      ? commandProgramNameInternal(
          commandSplit.remainingCommand,
          depth,
          context,
          segmentsRemaining - 1,
        )
      : null;
  }
  let index = 0;
  let wrapper: CommandWrapper | null = null;
  let executionContext = context;
  let sawAssignment = false;
  let sawRedirection = false;

  while (index < tokens.length) {
    const token = tokens[index];
    if (!token) return null;
    const indexAfterRedirection = indexAfterShellRedirection(tokens, index);
    if (indexAfterRedirection !== null) {
      if (indexAfterRedirection > tokens.length) return null;
      sawRedirection = true;
      index = indexAfterRedirection;
      continue;
    }
    if (/^[A-Za-z_][A-Za-z0-9_]*\+?=/.test(token)) {
      sawAssignment = true;
      index += 1;
      continue;
    }
    if (executionContext === "shell" && token === ":") {
      return commandSplit.remainingCommand
        ? commandProgramNameInternal(
            commandSplit.remainingCommand,
            depth,
            executionContext,
            segmentsRemaining - 1,
          )
        : null;
    }
    if (
      NON_PROGRAM_PREFIX_CHARACTERS.includes(token[0] ?? "") &&
      !(token.startsWith("$") && token.includes("/"))
    ) {
      return executionContext === "shell" && token.startsWith("[") && commandSplit.remainingCommand
        ? commandProgramNameInternal(
            commandSplit.remainingCommand,
            depth,
            executionContext,
            segmentsRemaining - 1,
          )
        : null;
    }
    const tokenProgram = token.split(/[\\/]/).at(-1);
    const isUnqualifiedToken = token === tokenProgram;
    if (tokenProgram === "env" || tokenProgram === "sudo") {
      wrapper = tokenProgram;
      executionContext = "exec";
      index += 1;
      continue;
    }
    if (wrapper !== null && token === "--") {
      wrapper = null;
      index += 1;
      continue;
    }
    if (wrapper !== null && token.startsWith("-")) {
      if (wrapper === "env" && (token === "-S" || token === "--split-string")) {
        const splitCommand = tokens[index + 1];
        return splitCommand
          ? commandProgramNameInternal(splitCommand, depth + 1, executionContext, segmentsRemaining)
          : null;
      }
      if (wrapper === "env" && token.startsWith("--split-string=")) {
        return commandProgramNameInternal(
          token.slice("--split-string=".length),
          depth + 1,
          executionContext,
          segmentsRemaining,
        );
      }
      if (COMMAND_WRAPPER_OPTIONS_WITH_VALUE[wrapper].has(token)) {
        if (tokens[index + 1] === undefined) return null;
        index += 2;
        continue;
      }
      if (COMMAND_WRAPPER_FLAGS[wrapper].has(token)) {
        index += 1;
        continue;
      }
      const equalsIndex = token.indexOf("=");
      if (token.startsWith("--") && equalsIndex > 2) {
        if (!COMMAND_WRAPPER_OPTIONS_WITH_VALUE[wrapper].has(token.slice(0, equalsIndex))) {
          return null;
        }
        index += 1;
        continue;
      }
      if (/^-[A-Za-z].+/.test(token) && !token.startsWith("--")) {
        let consumesNextToken = false;
        for (const [optionIndex, option] of token.slice(1).split("").entries()) {
          const shortOption = `-${option}`;
          if (COMMAND_WRAPPER_OPTIONS_WITH_VALUE[wrapper].has(shortOption)) {
            consumesNextToken = optionIndex === token.length - 2;
            break;
          }
          if (!COMMAND_WRAPPER_FLAGS[wrapper].has(shortOption)) return null;
        }
        if (consumesNextToken && tokens[index + 1] === undefined) return null;
        index += consumesNextToken ? 2 : 1;
        continue;
      }
      return null;
    }
    if (tokenProgram && SHELL_PROGRAMS.has(tokenProgram.replace(/\.exe$/i, "").toLowerCase())) {
      const scriptIndex = shellCommandArgumentIndex(tokens, index + 1);
      if (scriptIndex !== null) {
        const script = tokens[scriptIndex];
        return script
          ? commandProgramNameInternal(script, depth + 1, "shell", segmentsRemaining)
          : null;
      }
    }
    const lowerTokenProgram = tokenProgram?.toLowerCase();
    if (lowerTokenProgram && WINDOWS_SHELL_PROGRAMS.has(lowerTokenProgram)) {
      const payload = windowsShellPayloadProgramName(
        lowerTokenProgram,
        tokens,
        index + 1,
        depth,
        commandSplit.remainingCommand,
        commandSplit.separator,
        segmentsRemaining,
      );
      if (payload.matched) return payload.program;
    }
    if (lowerTokenProgram === "start-process") {
      const startedProgram = startProcessProgramName(tokens, index + 1);
      if (startedProgram !== null) return startedProgram;
    }
    if (
      executionContext === "shell" &&
      isUnqualifiedToken &&
      tokenProgram &&
      SHELL_PRECOMMAND_MODIFIERS.has(tokenProgram)
    ) {
      index += 1;
      if (tokenProgram === "time" && tokens[index] === "-p") index += 1;
      if (tokens[index]?.startsWith("-")) return null;
      continue;
    }
    if (isUnqualifiedToken && tokenProgram) {
      const targetIndex = transparentWrapperCommandIndex(tokenProgram, tokens, index);
      if (targetIndex !== null) {
        const wrappedProgram = commandProgramNameInternal(
          serializeShellTokens(tokens.slice(targetIndex)),
          depth + 1,
          "exec",
          segmentsRemaining,
        );
        if (wrappedProgram !== null) return wrappedProgram;
      }
    }
    if (isUnqualifiedToken && tokenProgram && SHELL_COMMAND_WRAPPERS.has(tokenProgram)) {
      return wrappedShellCommandProgramName(
        tokenProgram,
        tokens,
        index + 1,
        depth,
        commandSplit.remainingCommand,
        segmentsRemaining,
      );
    }
    if (
      (executionContext === "shell" ||
        (wrapper === "sudo" && tokenProgram && SKIPPABLE_SUDO_PROBES.has(tokenProgram))) &&
      isUnqualifiedToken &&
      tokenProgram &&
      NON_DESCRIPTIVE_SHELL_PROGRAMS.has(tokenProgram) &&
      (!TERMINAL_SHELL_PROGRAMS.has(tokenProgram) ||
        (tokenProgram === "false" && commandSplit.separator !== "&&")) &&
      commandSplit.remainingCommand
    ) {
      return commandProgramNameInternal(
        commandSplit.remainingCommand,
        depth,
        "shell",
        segmentsRemaining - 1,
      );
    }
    if (
      executionContext === "shell" &&
      isUnqualifiedToken &&
      lowerTokenProgram &&
      POWERSHELL_SETUP_PROGRAMS.has(lowerTokenProgram) &&
      commandSplit.remainingCommand
    ) {
      return commandProgramNameInternal(
        commandSplit.remainingCommand,
        depth,
        "shell",
        segmentsRemaining - 1,
      );
    }
    if (
      !tokenProgram ||
      (isUnqualifiedToken && NON_DESCRIPTIVE_SHELL_PROGRAMS.has(tokenProgram)) ||
      NON_PROGRAM_PREFIX_CHARACTERS.includes(tokenProgram[0] ?? "") ||
      NON_PROGRAM_SUFFIX_CHARACTERS.includes(tokenProgram.at(-1) ?? "") ||
      tokenProgram.endsWith("()") ||
      /^[A-Za-z_][A-Za-z0-9_]*\(\)\{$/u.test(tokenProgram)
    ) {
      return null;
    }
    return tokenProgram || null;
  }

  if ((sawAssignment || sawRedirection) && wrapper === null && commandSplit.remainingCommand) {
    return commandProgramNameInternal(
      commandSplit.remainingCommand,
      depth,
      executionContext,
      segmentsRemaining - 1,
    );
  }
  return null;
}
